import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "lucide-react";

interface CategoryItem {
  name: string;
  image: string | null;
  slug: string | null;
}

export const CategoryStrip = () => {
  const [items, setItems] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [{ data: cats }, { data: products }] = await Promise.all([
        supabase
          .from("categories")
          .select("name, image_url, slug, display_order, parent_id")
          .is("parent_id", null)
          .order("display_order", { ascending: true })
          .order("name"),
        supabase.from("products").select("category, image_url, images"),
      ]);

      const imageByCat = new Map<string, string>();
      (products ?? []).forEach((p: any) => {
        if (!p.category) return;
        if (imageByCat.has(p.category)) return;
        const img = p.image_url || (p.images && p.images[0]) || null;
        if (img) imageByCat.set(p.category, img);
      });

      const list: CategoryItem[] = (cats ?? []).map((c: any) => ({
        name: c.name,
        image: c.image_url || imageByCat.get(c.name) || null,
        slug: c.slug ?? null,
      }));
      setItems(list);
      setLoading(false);
    };
    load();
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section
      aria-labelledby="shop-by-category-heading"
      className="relative w-full bg-background py-5 md:py-8"
    >
      <div className="container">
        <div className="mb-4 md:mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
              Browse
            </p>
            <h2
              id="shop-by-category-heading"
              className="font-display text-xl md:text-2xl lg:text-3xl font-bold leading-tight"
            >
              Shop by Category
            </h2>
            <p className="mt-1 text-sm text-muted-foreground hidden sm:block">
              Quick access to our most-loved collections.
            </p>
          </div>
        </div>

        <div className="-mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth scrollbar-none snap-x snap-mandatory pb-2 pr-6 md:pr-0">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="shrink-0 w-[88px] sm:w-[100px] md:w-[120px] lg:w-[130px] space-y-2"
                  >
                    <Skeleton className="h-[88px] w-[88px] sm:h-[100px] sm:w-[100px] md:h-[120px] md:w-[120px] lg:h-[130px] lg:w-[130px] rounded-2xl" />
                    <Skeleton className="h-3 w-3/4 mx-auto" />
                  </div>
                ))
              : items.map((c) => (
                  <Link
                    key={c.name}
                    to={`/category/${encodeURIComponent(c.slug || c.name)}`}
                    className="group shrink-0 w-[88px] sm:w-[100px] md:w-[120px] lg:w-[130px] snap-start text-center"
                  >
                    <div className="relative h-[88px] w-[88px] sm:h-[100px] sm:w-[100px] md:h-[120px] md:w-[120px] lg:h-[130px] lg:w-[130px] overflow-hidden rounded-2xl border border-border/60 bg-secondary shadow-soft transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-hover group-hover:border-primary/40">
                      {c.image ? (
                        <img
                          src={c.image}
                          alt={c.name}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center bg-primary/10 text-primary">
                          <Tag className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                    <p className="mt-2 truncate text-xs md:text-sm font-medium text-foreground/85 group-hover:text-primary">
                      {c.name}
                    </p>
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
};
